import { Button, TextInput } from '@mantine/core';
import { IconFileTypeXls } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import i18n from 'i18next';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getDepartmentAsHeadOfOptions } from '@/module/Department/model/DepartmentQuery';
import { getDisciplineAsHeadOfOptions } from '@/module/Discipline/model/DisciplineQuery';
import { FacultyFilter } from '@/module/Nomen/Faculty/FacultyFilter';
import { facultyOptions } from '@/module/Nomen/Faculty/model/FacultyModel';
import { useTravelFacultyFilter } from '@/module/Travel/Registry/useTravelFacultyFilter';
import { isTravelDayBalancePeopleRole } from '@/module/Travel/travelRoles';

import { RoleType, useCurrentUser } from '@feature/security';
import { useAppNotification } from '@feature/utils';

import { TravelDayBalancePersonSelect } from './components/TravelDayBalancePersonSelect';
import { TravelOwnDayBalancePanel } from './components/TravelOwnDayBalancePanel';
import { useTravelDayBalancePeopleExport } from './model/TravelRequestQuery';
import { TravelDayBalancePerson } from './model/TravelRequestTypes';

export function TravelDays() {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const { id, firstName, lastName, currentRole } = useCurrentUser();
  const [year, setYear] = useState(new Date().getFullYear());
  const [person, setPerson] = useState<TravelDayBalancePerson | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [facultyTouched, setFacultyTouched] = useState(false);
  const { facultyId: pickedFacultyId, facultyFilterProps } = useTravelFacultyFilter(true);
  const exportPeople = useTravelDayBalancePeopleExport();
  const canPick = isTravelDayBalancePeopleRole(currentRole) && !unavailable;
  const { data: faculties } = useQuery({ ...facultyOptions.getAll(), enabled: canPick && facultyFilterProps != null });
  const allowedIds = facultyFilterProps?.allowedIds;
  const firstFaculty = faculties?.find(faculty => allowedIds == null || allowedIds.includes(faculty.id));
  // Until the user touches the filter it shows the first faculty, while a cleared filter still means the whole scope.
  const facultyId = facultyTouched || facultyFilterProps == null ? pickedFacultyId : (firstFaculty?.id ?? null);
  const departmentHead = currentRole === RoleType.DEPARTMENT_ADMIN;
  const disciplineHead = currentRole === RoleType.DISCIPLINE_ADMIN;
  const { data: departments } = useQuery({ ...getDepartmentAsHeadOfOptions(), enabled: canPick && departmentHead });
  const { data: disciplines } = useQuery({ ...getDisciplineAsHeadOfOptions(), enabled: canPick && disciplineHead });
  const headedUnits = departmentHead ? departments : disciplines;

  const onUnavailable = useCallback(() => {
    setUnavailable(true);
    setPerson(null);
  }, []);

  const onExport = () =>
    exportPeople.mutate(
      { year, facultyId, lang: i18n.language === 'en' ? 'en' : 'ro' },
      { onError: notification.error },
    );

  return (
    <TravelOwnDayBalancePanel
      year={year}
      onYearChange={setYear}
      userId={canPick ? person?.userId : null}
      title={
        canPick && person != null ? t('travel.requests.dayBalance.personTitle', { name: person.fullName }) : undefined
      }
      controls={
        canPick && (
          <>
            {facultyFilterProps && (
              <FacultyFilter
                label={t('travel.registry.filters.faculty')}
                {...facultyFilterProps}
                value={facultyId == null ? null : String(facultyId)}
                onChange={value => {
                  setFacultyTouched(true);
                  facultyFilterProps.onChange(value);
                  setPerson(null);
                }}
              />
            )}
            {(departmentHead || disciplineHead) && headedUnits != null && headedUnits.length > 0 && (
              <TextInput
                label={t(departmentHead ? 'hierarchy.department' : 'hierarchy.discipline')}
                value={headedUnits.map(unit => unit.name).join(', ')}
                disabled
                readOnly
              />
            )}
            <TravelDayBalancePersonSelect
              year={year}
              facultyId={facultyId}
              selfId={id}
              selfName={`${lastName} ${firstName}`}
              value={person}
              onChange={setPerson}
              onUnavailable={onUnavailable}
            />
            <Button
              variant="outline"
              leftSection={<IconFileTypeXls size={14} />}
              loading={exportPeople.isPending}
              onClick={onExport}
            >
              {t('travel.registry.export')}
            </Button>
          </>
        )
      }
    />
  );
}
